import React, { useState, useMemo } from "react";
import Page from "../components/Page";
import {
  IconPlus,
  IconFolderPlus,
  IconSearch,
  IconChevronRight,
  IconChevronDown,
  IconFolders,
  IconFileText,
  IconCheck,
  IconX,
  IconEdit,
  IconShield,
  IconBuildingBank,
  IconArrowUpRight,
  IconArrowDownLeft
} from "@tabler/icons-react";
import {
  useChartOfAccountsTree,
  createAccountGroup,
  createLedgerAccount,
  updateLedgerAccount
} from "../controllers/accounting.controller";
import toast from "react-hot-toast";

const ROOT_TYPES = [
  { key: "ASSET", label: "Assets", code: "1000", badgeColor: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200" },
  { key: "LIABILITY", label: "Liabilities", code: "2000", badgeColor: "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200" },
  { key: "EQUITY", label: "Equity", code: "3000", badgeColor: "bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200" },
  { key: "INCOME", label: "Income", code: "4000", badgeColor: "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200" },
  { key: "EXPENSE", label: "Expenses", code: "5000", badgeColor: "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200" }
];

export default function ChartOfAccountsPage() {
  const { tree, groups, flatAccounts, isLoading, mutate } = useChartOfAccountsTree();

  const [search, setSearch] = useState("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState("ALL");
  const [expandedNodes, setExpandedNodes] = useState({});

  // Modal forms
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [groupForm, setGroupForm] = useState({
    group_name: "",
    group_code: "",
    account_type: "ASSET",
    parent_group_id: ""
  });

  const [ledgerForm, setLedgerForm] = useState({
    account_name: "",
    account_code: "",
    account_type: "ASSET",
    group_id: "",
    opening_balance: 0,
    opening_balance_type: "DEBIT",
    is_bank_cash: false,
    description: ""
  });

  const [editingLedger, setEditingLedger] = useState(null);

  // Toggle node expansion
  const toggleNode = (nodeKey) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeKey]: !prev[nodeKey]
    }));
  };

  // Expand all / collapse all
  const expandAll = () => {
    const all = {};
    const recurse = (list) => {
      list.forEach((item) => {
        all[item.key] = true;
        if (item.children) recurse(item.children);
      });
    };
    recurse(tree);
    setExpandedNodes(all);
  };

  const collapseAll = () => {
    setExpandedNodes({});
  };

  // Filtered Tree by Root Type & Search
  const filteredTree = useMemo(() => {
    if (!tree || !Array.isArray(tree)) return [];

    let filtered = tree;
    if (selectedTypeFilter !== "ALL") {
      filtered = tree.filter((t) => t.account_type === selectedTypeFilter);
    }

    if (!search.trim()) return filtered;

    const q = search.toLowerCase();

    // Recursive search filter
    const filterItem = (node) => {
      const matchSelf =
        node.name?.toLowerCase().includes(q) ||
        node.code?.toLowerCase().includes(q);

      let matchedChildren = [];
      if (node.children && node.children.length > 0) {
        matchedChildren = node.children
          .map(filterItem)
          .filter(Boolean);
      }

      if (matchSelf || matchedChildren.length > 0) {
        return {
          ...node,
          children: matchedChildren
        };
      }
      return null;
    };

    return filtered.map(filterItem).filter(Boolean);
  }, [tree, selectedTypeFilter, search]);

  // Handle Create Group
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.group_name.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    try {
      setIsSubmitting(true);
      toast.loading("Creating Account Group...");
      await createAccountGroup(groupForm);
      toast.dismiss();
      toast.success("Account group created successfully!");
      mutate();
      document.getElementById("modal-create-group").close();
      setGroupForm({
        group_name: "",
        group_code: "",
        account_type: "ASSET",
        parent_group_id: ""
      });
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Failed to create group");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Create Ledger
  const handleCreateLedger = async (e) => {
    e.preventDefault();
    if (!ledgerForm.account_name.trim()) {
      toast.error("Please enter an account name");
      return;
    }

    try {
      setIsSubmitting(true);
      toast.loading("Creating Ledger Account...");
      await createLedgerAccount(ledgerForm);
      toast.dismiss();
      toast.success("Ledger account created successfully!");
      mutate();
      document.getElementById("modal-create-ledger").close();
      setLedgerForm({
        account_name: "",
        account_code: "",
        account_type: "ASSET",
        group_id: "",
        opening_balance: 0,
        opening_balance_type: "DEBIT",
        is_bank_cash: false,
        description: ""
      });
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Failed to create ledger");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Update Ledger
  const handleUpdateLedger = async (e) => {
    e.preventDefault();
    if (!editingLedger) return;

    try {
      setIsSubmitting(true);
      toast.loading("Updating Ledger Account...");
      await updateLedgerAccount(editingLedger.id, editingLedger);
      toast.dismiss();
      toast.success("Ledger account updated successfully!");
      mutate();
      document.getElementById("modal-edit-ledger").close();
      setEditingLedger(null);
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Failed to update ledger");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render tree node component
  const renderTreeNode = (node, depth = 0) => {
    const isExpanded = expandedNodes[node.key] || Boolean(search.trim());
    const hasChildren = node.children && node.children.length > 0;
    const isLeafLedger = node.is_ledger;

    return (
      <div key={node.key} className="select-none">
        <div
          className={`flex items-center justify-between py-2.5 px-3 rounded-2xl hover:bg-gray-100/70 dark:hover:bg-gray-800/50 transition cursor-pointer ${
            depth === 0 ? "bg-gray-50/70 dark:bg-gray-850/40 my-1 font-black" : "my-0.5"
          }`}
          style={{ paddingLeft: `${Math.max(12, depth * 24)}px` }}
          onClick={() => (hasChildren ? toggleNode(node.key) : null)}
        >
          <div className="flex items-center gap-2 min-w-0">
            {hasChildren ? (
              <button
                type="button"
                className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
              >
                {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              </button>
            ) : (
              <span className="w-5 shrink-0"></span>
            )}

            {depth === 0 ? (
              <IconFolders size={18} className="text-purple-600 shrink-0" />
            ) : isLeafLedger ? (
              <IconFileText size={16} className="text-emerald-500 shrink-0" />
            ) : (
              <IconFolders size={16} className="text-blue-500 shrink-0" />
            )}

            <div className="flex items-center gap-2 truncate">
              <span className="font-mono text-xs font-bold text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                {node.code || "—"}
              </span>
              <span
                className={`text-xs truncate ${
                  depth === 0
                    ? "font-black text-gray-900 dark:text-white uppercase tracking-wider text-sm"
                    : isLeafLedger
                    ? "font-bold text-gray-800 dark:text-gray-200"
                    : "font-bold text-gray-700 dark:text-gray-300"
                }`}
              >
                {node.name}
              </span>
            </div>

            {/* Badges */}
            <div className="hidden sm:flex items-center gap-1.5 ml-2">
              {node.is_control_account && (
                <span className="text-[10px] px-2 py-0.2 rounded-full font-black bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 flex items-center gap-0.5">
                  <IconShield size={10} /> Control
                </span>
              )}
              {node.is_bank_cash && (
                <span className="text-[10px] px-2 py-0.2 rounded-full font-black bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 flex items-center gap-0.5">
                  <IconBuildingBank size={10} /> Bank/Cash
                </span>
              )}
            </div>
          </div>

          {/* Right Info & Actions */}
          <div className="flex items-center gap-3 shrink-0">
            {isLeafLedger && (
              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-900 dark:text-white">
                  Rs. {Number(node.current_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-[10px] ml-1 font-bold text-gray-400">
                  {node.normal_balance === "DEBIT" ? "Dr" : "Cr"}
                </span>
              </div>
            )}

            {isLeafLedger && !node.is_system && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingLedger({ ...node });
                  document.getElementById("modal-edit-ledger").showModal();
                }}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white transition"
              >
                <IconEdit size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Children Render */}
        {hasChildren && isExpanded && (
          <div className="space-y-0.5">{node.children.map((child) => renderTreeNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <Page>
      {/* ─── HEADER BANNER ─── */}
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 rounded-3xl p-6 md:p-8 text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-xs font-black uppercase tracking-wider mb-2">
              <IconFolders size={14} /> Chartered Standard Architecture
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">Chart of Accounts</h1>
            <p className="text-gray-300 text-sm mt-1 max-w-xl">
              Hierarchical 5-root accounting architecture (Assets, Liabilities, Equity, Income, Expenses). Supports unlimited nested groups, sub-accounts, and automated control ledgers.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => document.getElementById("modal-create-group").showModal()}
              className="py-3 px-5 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-black text-xs flex items-center gap-2 transition border border-white/20 active:scale-95"
            >
              <IconFolderPlus size={18} /> New Account Group
            </button>
            <button
              onClick={() => document.getElementById("modal-create-ledger").showModal()}
              className="py-3 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black text-xs flex items-center gap-2 transition shadow-lg shadow-emerald-900/30 active:scale-95"
            >
              <IconPlus size={18} /> New Ledger Account
            </button>
          </div>
        </div>

        {/* Controls: Search, Root Type Filter Pills, Expand/Collapse */}
        <div className="bg-white dark:bg-restro-gray border border-gray-100 dark:border-gray-800 rounded-3xl p-5 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Search Box */}
            <div className="relative flex-1 max-w-md">
              <IconSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search Account Code, Name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl py-2 pl-10 pr-4 text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>

            {/* Tree helpers */}
            <div className="flex items-center gap-2">
              <button
                onClick={expandAll}
                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition"
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Root Type Filter Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
            <button
              onClick={() => setSelectedTypeFilter("ALL")}
              className={`px-4 py-2 rounded-xl text-xs font-black shrink-0 transition ${
                selectedTypeFilter === "ALL"
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              All Types
            </button>
            {ROOT_TYPES.map((t) => (
              <button
                key={t.key}
                onClick={() => setSelectedTypeFilter(t.key)}
                className={`px-4 py-2 rounded-xl text-xs font-bold shrink-0 transition flex items-center gap-1.5 ${
                  selectedTypeFilter === t.key
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/20 font-black"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                <span>{t.label}</span>
                <span className="font-mono text-[10px] opacity-70">({t.code})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tree Container */}
        <div className="bg-white dark:bg-restro-gray rounded-3xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm">
          {isLoading ? (
            <div className="py-16 text-center text-gray-400">
              <span className="loading loading-spinner loading-md"></span>
              <p className="mt-2 text-xs">Loading Chart of Accounts Tree...</p>
            </div>
          ) : filteredTree.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-xs">
              No matching accounts or groups found.
            </div>
          ) : (
            <div className="space-y-1">{filteredTree.map((rootNode) => renderTreeNode(rootNode, 0))}</div>
          )}
        </div>
      </div>

      {/* ─── MODAL: CREATE ACCOUNT GROUP ─── */}
      <dialog id="modal-create-group" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md">
          <div className="bg-blue-600 p-6 text-white relative">
            <h3 className="text-xl font-black mb-0.5">Create Account Group</h3>
            <p className="text-blue-100 text-xs">Add a category or sub-category to organize ledgers</p>
          </div>

          <form onSubmit={handleCreateGroup} className="p-6 space-y-4">
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Account Type (5 Root Types)
              </label>
              <select
                value={groupForm.account_type}
                onChange={(e) => setGroupForm({ ...groupForm, account_type: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
              >
                {ROOT_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label} ({t.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Parent Group (Optional for sub-group)
              </label>
              <select
                value={groupForm.parent_group_id}
                onChange={(e) => setGroupForm({ ...groupForm, parent_group_id: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
              >
                <option value="">None (Top-Level Group)</option>
                {groups
                  ?.filter((g) => g.account_type === groupForm.account_type)
                  .map((g) => (
                    <option key={g.id} value={g.id}>
                      [{g.group_code}] {g.group_name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Group Name (e.g. Current Assets)
              </label>
              <input
                type="text"
                required
                value={groupForm.group_name}
                onChange={(e) => setGroupForm({ ...groupForm, group_name: e.target.value })}
                placeholder="Group Name"
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Group Code (Optional, e.g. 1100)
              </label>
              <input
                type="text"
                value={groupForm.group_code}
                onChange={(e) => setGroupForm({ ...groupForm, group_code: e.target.value })}
                placeholder="Auto-generated if empty"
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold font-mono outline-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-create-group").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-3 font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-2xl flex items-center justify-center gap-1.5 shadow-lg shadow-blue-200 dark:shadow-none"
              >
                {isSubmitting ? <span className="loading loading-spinner loading-xs"></span> : <IconCheck size={16} />}
                Create Group
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ─── MODAL: CREATE LEDGER ACCOUNT ─── */}
      <dialog id="modal-create-ledger" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md">
          <div className="bg-emerald-600 p-6 text-white relative">
            <h3 className="text-xl font-black mb-0.5">Create Ledger Account</h3>
            <p className="text-emerald-100 text-xs">Add an individual ledger account for double-entry postings</p>
          </div>

          <form onSubmit={handleCreateLedger} className="p-6 space-y-4">
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Account Type
              </label>
              <select
                value={ledgerForm.account_type}
                onChange={(e) => setLedgerForm({ ...ledgerForm, account_type: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
              >
                {ROOT_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label} ({t.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Parent Account Group
              </label>
              <select
                value={ledgerForm.group_id}
                onChange={(e) => setLedgerForm({ ...ledgerForm, group_id: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
              >
                <option value="">Select Parent Group</option>
                {groups
                  ?.filter((g) => g.account_type === ledgerForm.account_type)
                  .map((g) => (
                    <option key={g.id} value={g.id}>
                      [{g.group_code}] {g.group_name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                Account Name (e.g. Nabil Bank A/C)
              </label>
              <input
                type="text"
                required
                value={ledgerForm.account_name}
                onChange={(e) => setLedgerForm({ ...ledgerForm, account_name: e.target.value })}
                placeholder="Account Name"
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                  Account Code
                </label>
                <input
                  type="text"
                  value={ledgerForm.account_code}
                  onChange={(e) => setLedgerForm({ ...ledgerForm, account_code: e.target.value })}
                  placeholder="Auto if empty"
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold font-mono outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                  Opening Balance
                </label>
                <input
                  type="number"
                  step="any"
                  value={ledgerForm.opening_balance}
                  onChange={(e) => setLedgerForm({ ...ledgerForm, opening_balance: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="cb-is-bank-cash"
                checked={ledgerForm.is_bank_cash}
                onChange={(e) => setLedgerForm({ ...ledgerForm, is_bank_cash: e.target.checked })}
                className="checkbox checkbox-sm checkbox-success rounded-lg"
              />
              <label htmlFor="cb-is-bank-cash" className="text-xs font-bold text-gray-700 dark:text-gray-300 cursor-pointer">
                Is Cash / Bank Account (Appears in Payment/Receipt vouchers)
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-create-ledger").close()}
                className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-3 font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-700 rounded-2xl flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-200 dark:shadow-none"
              >
                {isSubmitting ? <span className="loading loading-spinner loading-xs"></span> : <IconCheck size={16} />}
                Create Ledger
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ─── MODAL: EDIT LEDGER ACCOUNT ─── */}
      {editingLedger && (
        <dialog id="modal-edit-ledger" className="modal modal-bottom sm:modal-middle">
          <div className="modal-box p-0 border border-gray-100 dark:border-gray-800 rounded-3xl overflow-hidden max-w-md">
            <div className="bg-gray-900 p-6 text-white relative">
              <h3 className="text-xl font-black mb-0.5">Edit Ledger Account</h3>
              <p className="text-gray-400 text-xs">Modify account details and classification</p>
            </div>

            <form onSubmit={handleUpdateLedger} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                  Account Name
                </label>
                <input
                  type="text"
                  required
                  value={editingLedger.name}
                  onChange={(e) => setEditingLedger({ ...editingLedger, name: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                  Account Code
                </label>
                <input
                  type="text"
                  value={editingLedger.code}
                  onChange={(e) => setEditingLedger({ ...editingLedger, code: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold font-mono outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-1 block">
                  Parent Group
                </label>
                <select
                  value={editingLedger.group_id || ""}
                  onChange={(e) => setEditingLedger({ ...editingLedger, group_id: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl py-2.5 px-4 text-xs font-bold outline-none"
                >
                  <option value="">Select Group</option>
                  {groups
                    ?.filter((g) => g.account_type === editingLedger.account_type)
                    .map((g) => (
                      <option key={g.id} value={g.id}>
                        [{g.group_code}] {g.group_name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => document.getElementById("modal-edit-ledger").close()}
                  className="flex-1 py-3 font-bold text-xs text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-2xl hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-2xl flex items-center justify-center gap-1.5 shadow-lg shadow-blue-200 dark:shadow-none"
                >
                  {isSubmitting ? <span className="loading loading-spinner loading-xs"></span> : <IconCheck size={16} />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </Page>
  );
}
